/**
 * Domain events (docs/55 §2.1). An event says that something happened and names it by id. Events never carry
 * consumer contact data (names, phones, emails, message bodies): consumers read those through the data layer
 * with a tenant context. A test fails if any payload grows such a field.
 */
import { z } from 'zod';
import { Actor } from './actor.ts';
import { IsoDateTime, PositiveCents } from './common.ts';
import {
  ApprovalOutcome,
  ApprovalSubjectType,
  ApproverKind,
  Channel,
  ExportKind,
  LeadSource,
  LeadStatus,
  OnboardingStep,
  OnboardingStepState,
  Plan,
  RequestAuthorKind,
  RequestChannel,
  RequestStatus,
  SuppressionReason,
} from './enums.ts';
import {
  ApprovalId,
  DeliveryId,
  EventId,
  ExportId,
  LeadId,
  LocationId,
  OutputId,
  ProposalId,
  RequestId,
  ReviewId,
  ReviewRequestId,
  SiteId,
  TenantId,
} from './ids.ts';

const ev = <T extends string, P extends z.ZodType>(type: T, payload: P) =>
  z.object({
    id: EventId,
    type: z.literal(type),
    v: z.literal(1),
    at: IsoDateTime,
    tenantId: TenantId,
    locationId: LocationId.nullable(),
    actor: Actor,
    payload,
  });

export const EVENT_SCHEMAS = {
  'lead.created': ev('lead.created', z.object({ leadId: LeadId, source: LeadSource, spam: z.boolean(), serviceSlug: z.string().max(64).nullable() })),
  'lead.status_changed': ev('lead.status_changed', z.object({ leadId: LeadId, from: LeadStatus, to: LeadStatus })),
  'review.new': ev('review.new', z.object({ reviewId: ReviewId, rating: z.number().int().min(1).max(5) })),
  'review.reply_posted': ev('review.reply_posted', z.object({ reviewId: ReviewId, outputId: OutputId.nullable() })),
  'review_request.scheduled': ev('review_request.scheduled', z.object({ reviewRequestId: ReviewRequestId, scheduledFor: IsoDateTime, channel: Channel })),
  'review_request.sent': ev('review_request.sent', z.object({ reviewRequestId: ReviewRequestId, touch: z.enum(['first', 'reminder']), channel: Channel, deliveryId: DeliveryId })),
  'review_request.opted_out': ev('review_request.opted_out', z.object({ reviewRequestId: ReviewRequestId.nullable(), channel: z.enum(['sms', 'email', 'all']), reason: SuppressionReason })),
  'request.created': ev('request.created', z.object({ requestId: RequestId, number: z.number().int().positive(), channel: RequestChannel, authorKind: RequestAuthorKind })),
  'request.updated': ev('request.updated', z.object({ requestId: RequestId, from: RequestStatus, to: RequestStatus })),
  'proposal.ready': ev('proposal.ready', z.object({ proposalId: ProposalId, requestId: RequestId, needsClientApproval: z.boolean() })),
  'approval.recorded': ev('approval.recorded', z.object({ approvalId: ApprovalId, subjectType: ApprovalSubjectType, subjectId: z.string().max(64), decision: ApprovalOutcome, approverKind: ApproverKind })),
  'version.ready': ev('version.ready', z.object({ siteId: SiteId, version: z.number().int().min(1) })),
  'version.live': ev('version.live', z.object({ siteId: SiteId, version: z.number().int().min(1), previousVersion: z.number().int().min(1).nullable() })),
  'version.rolled_back': ev('version.rolled_back', z.object({ siteId: SiteId, fromVersion: z.number().int().min(1), toVersion: z.number().int().min(1), automatic: z.boolean() })),
  'onboarding.step_changed': ev('onboarding.step_changed', z.object({ step: OnboardingStep, from: OnboardingStepState.nullable(), to: OnboardingStepState })),
  'billing.setup_paid': ev('billing.setup_paid', z.object({ plan: Plan, amountCents: PositiveCents })),
  'billing.subscription_started': ev('billing.subscription_started', z.object({ plan: Plan, firstInvoiceCents: PositiveCents })),
  'billing.payment_failed': ev('billing.payment_failed', z.object({ attempt: z.number().int().min(1), dunningDay: z.number().int().min(0) })),
  'billing.canceled': ev('billing.canceled', z.object({ effectiveAt: IsoDateTime })),
  'export.ready': ev('export.ready', z.object({ exportId: ExportId, kind: ExportKind, expiresAt: IsoDateTime })),
} as const;

export type EventType = keyof typeof EVENT_SCHEMAS;
export const EVENT_TYPES = Object.keys(EVENT_SCHEMAS) as EventType[];
export const EventType = z.enum(EVENT_TYPES as [EventType, ...EventType[]]);

export const DomainEvent = z.discriminatedUnion('type', [
  EVENT_SCHEMAS['lead.created'],
  EVENT_SCHEMAS['lead.status_changed'],
  EVENT_SCHEMAS['review.new'],
  EVENT_SCHEMAS['review.reply_posted'],
  EVENT_SCHEMAS['review_request.scheduled'],
  EVENT_SCHEMAS['review_request.sent'],
  EVENT_SCHEMAS['review_request.opted_out'],
  EVENT_SCHEMAS['request.created'],
  EVENT_SCHEMAS['request.updated'],
  EVENT_SCHEMAS['proposal.ready'],
  EVENT_SCHEMAS['approval.recorded'],
  EVENT_SCHEMAS['version.ready'],
  EVENT_SCHEMAS['version.live'],
  EVENT_SCHEMAS['version.rolled_back'],
  EVENT_SCHEMAS['onboarding.step_changed'],
  EVENT_SCHEMAS['billing.setup_paid'],
  EVENT_SCHEMAS['billing.subscription_started'],
  EVENT_SCHEMAS['billing.payment_failed'],
  EVENT_SCHEMAS['billing.canceled'],
  EVENT_SCHEMAS['export.ready'],
]);
export type DomainEvent = z.infer<typeof DomainEvent>;
export type EventOf<T extends EventType> = Extract<DomainEvent, { type: T }>;
export type EventPayload<T extends EventType> = EventOf<T>['payload'];
